import React, { useState, useCallback, useEffect, useRef } from "react";
import { Button } from "antd";
import { LeftOutlined } from "@ant-design/icons";
import styled from "styled-components";
import type { FormInstance } from "antd/es/form";
import { AppButton } from "site/ui/base";
import { CourseContentTab, CourseContentTabProps } from "./CourseContent";
import { ICourse, ILessonChanges, IModule, IModuleChanges } from "utils/types";
import { CourseBasicForm } from "./CourseBasicForm";
import { AdditionalInfo } from "./AdditionalInfoForm";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 20px 40px;
  gap: 24px;
  font-family: "Manrope", sans-serif;

  @media (max-width: 768px) {
    padding: 15px;
  }
`;

const NavHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const BackButton = styled(Button)`
  display: flex;
  align-items: center;
  color: #5575f7;
  font-size: 14px;
  padding: 0;
  margin-right: 8px;
  border: none;
  font-family: "Manrope", sans-serif;
  box-shadow: none;

  &:hover {
    color: #4563e6;
    background: none;
  }
`;

const Title = styled.h1`
  font-family: Manrope-Medium;
  font-size: 24px;
  line-height: 32px;
  color: #333;
  margin: 0;
`;

const TabsContainer = styled.div`
  display: flex;
  gap: 24px;
  margin-bottom: 32px;
  position: relative;

  &::after {
    content: "";
    position: absolute;
    bottom: -12px;
    left: 0;
    width: 100%;
    height: 1px;
    background-color: #e0e0e0;
  }
`;

const Tab = styled.div<{ active: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
`;

const TabCircle = styled.div<{ active: boolean }>`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: ${(props) => (props.active ? "#5575f7" : "#e0e0e0")};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 12px;
  margin-bottom: 8px;
`;

const TabLabel = styled.span<{ active: boolean }>`
  font-size: 14px;
  color: ${(props) => (props.active ? "#333" : "#999")};
  font-weight: ${(props) => (props.active ? "500" : "normal")};
`;


const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  margin-top: 32px;
`;


export interface CreateCourseProps extends Required<Pick<CourseContentTabProps, "onDeleteModule">> {
  course?: ICourse;
  isEdit?: boolean;
  onCreateCourse?: (data: {course: ICourse; imageFile: File; courseModules: IModule[];}) => Promise<ICourse|undefined>;
  onBack: () => void;
  onUpdateCourse?: ( data: {
    courseChanges?: Partial<ICourse>;
    imageFile?: File;
    moduleChanges?: IModuleChanges;
    lessonChanges?: ILessonChanges;
  }) => Promise<ICourse|undefined>;
  onGetCourseModules?: (courseId: string) => Promise<IModule[]>;
  
}
export const CreateCourse: React.FC<CreateCourseProps> = ({
  onCreateCourse, course: initCourse, onBack, onUpdateCourse,
  onGetCourseModules, onDeleteModule, isEdit,
}) => {

  const courseFormRef = useRef<FormInstance<ICourse>>(null);
  const contentFormRef = useRef<FormInstance<{modules: IModule[]}>>(null);
  const extraFormRef = useRef<FormInstance<ICourse>>(null)

  const [processing, setProcessing] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<number>(0);
  const [modules, setModules] = useState<Partial<IModule>[]>(initCourse?.courseModules || [
    {
      id: "module-1",
      index: 1,
      title: "",
      description: "",
      lessons: [{ index: 1, id: "lesson-1", title: "", content: "", moduleId: "" }],
      activeKey: ["lesson-1"],
      courseId: initCourse?.id as string
    },
  ]);
  const [course, setCourse] = useState<ICourse | undefined>(initCourse)
  const [courseChanges, setCourseChanges] = useState<Partial<ICourse>>({})
  const [moduleChanges, setModuleChanges] = useState<IModuleChanges>({})
  const [lessonChanges, setLessonChanges] = useState<ILessonChanges>({})
  const [deletedModuleIds, setDeletedModuleIds] = useState<string[]>([])

  const [imageFile, setImageFile] = useState<File>()

  const handleUpdate = useCallback(async () => {
    switch(activeTab){
      case 0: {
        setActiveTab(1);
        break;
      }
      case 1: {
        setActiveTab(2);
        break;
      }
      case 2: {
        try{
          setProcessing(true)
          // TODO: provide a single endpoint for multiple delete 
          if(deletedModuleIds.length > 0){
            for(const moduleId of deletedModuleIds){
              await onDeleteModule?.(moduleId)
            }
          }
          const data = await onUpdateCourse?.({
            courseChanges,
            imageFile,
            moduleChanges,
            lessonChanges
          })
          onBack();
        }catch(e){
          console.log("Validate Failed:", e);
        }finally{
          setProcessing(false)
        }
        break;
      }
    }
      
  }, [
    activeTab, JSON.stringify(courseChanges), JSON.stringify(imageFile), JSON.stringify(moduleChanges), JSON.stringify(lessonChanges),
    JSON.stringify(deletedModuleIds),
  ]);

  const setTab = useCallback((tab: number) => {
    if(tab > activeTab){
      handleSubmit(false)
    }else {
      onStepBack(false)
    }
    setActiveTab(tab)
  }, [activeTab, JSON.stringify(course), JSON.stringify(modules)]);

  const handleSubmit = useCallback(async (shouldUpdate?: boolean) => {
    try{
      switch(activeTab){
        case 0: {
          const forData = await courseFormRef.current?.validateFields();
          let courseData:Partial<ICourse> = {}
          if(course){
            courseData = {
              ...courseData,
              ...course,
            }
          }
          if(forData){
            courseData = {
              ...courseData,
              ...forData,
            }
          }
          setCourse(courseData as ICourse)    
          if(shouldUpdate){
            setActiveTab(1);
          }      
          break;
        }
        case 1: {
          const data = await contentFormRef.current?.validateFields()
          const newData = modules?.map((item:Partial<IModule>, index) => ({
            ...item,
            ...data?.modules?.[index] || {},
            lessons: item?.lessons?.map(
              (lesson, lessonIndex) => ({
                ...lesson,
                ...data?.modules?.[index]?.lessons?.[lessonIndex] || {},
              })
            ),
            activeKey: item?.lessons?.length ? [item?.lessons?.[0]?.id || "lesson-1"] : [],
          }))
          setModules(newData)
          if(shouldUpdate){
            setActiveTab(2);
          }
          break;
        }
        case 2: {
          const formData = await extraFormRef.current?.validateFields()
          if(course){
            const data:ICourse = {
              ...course, 
              ...formData,
            }
            setProcessing(true)
            const newCourse = await onCreateCourse?.({course: data, imageFile: imageFile as File, courseModules: modules as IModule[]})
            setProcessing(false)
            if(newCourse){
              onBack()
            }
          }
        }
      }
    }catch(e){
      console.log("Validate Failed:", e);
    }
  }, [
    activeTab, JSON.stringify(course), JSON.stringify(modules)
  ]);
  const onStepBack = useCallback(async (shouldUpdate?: boolean) => {
    try{
      switch(activeTab){
        case 1: {
          const data = await contentFormRef.current?.getFieldsValue()
          if(course){
            courseFormRef.current?.setFieldsValue(course)
          }
          if(data?.modules){
            setModules(data?.modules)
          }
          break;
        }
        case 2: {
          const formData = await extraFormRef.current?.getFieldsValue()
          setCourse({
            ...((course || {}) as ICourse),
            ...(formData || {}),
          })
        }
      }
      if(shouldUpdate){
        setActiveTab(activeTab - 1)
      }
    }catch(e){
      console.log("Validate Failed:", e);
    }
  }, [
    activeTab, JSON.stringify(course), JSON.stringify(modules)
  ]);

  useEffect(() => {
    if(isEdit && initCourse){
      onGetCourseModules?.(initCourse.id as string).then(modules => {
        const newModules = structuredClone(modules) as IModule[]
        setModules(newModules.map(item => ({
          ...item, activeKey: [item.lessons?.[0]?.id as string]
        })))
      })
    }
  }, [isEdit, JSON.stringify(initCourse)])

  return (
    <Container>
      <NavHeader>
        <BackButton type="text" icon={<LeftOutlined />} onClick={onBack}>
          Course management
        </BackButton>
        <AppButton variant="primary">Publish Course</AppButton>
      </NavHeader>

      <Title>Create course</Title>

      <TabsContainer>
        <Tab active={activeTab === 0} onClick={() => setTab(0)}>
          <TabCircle active={activeTab === 0}>1</TabCircle>
          <TabLabel active={activeTab === 0}>Basic Details</TabLabel>
        </Tab>
        <Tab active={activeTab === 1} onClick={() => setTab(1)}>
          <TabCircle active={activeTab === 1}>2</TabCircle>
          <TabLabel active={activeTab === 1}>Course content</TabLabel>
        </Tab>
        <Tab active={activeTab === 2} onClick={() => setTab(2)}>
          <TabCircle active={activeTab === 2}>3</TabCircle>
          <TabLabel active={activeTab === 2}>Additional Information</TabLabel>
        </Tab>
      </TabsContainer>

      <div style={{ display: "flex", gap: "24px", justifyContent: "center", width: "100%" }}>
        <div style={{display: "flex", maxWidth: 800,  flexDirection: "column", width: "100%" }}>
          { activeTab === 0 && (
              <CourseBasicForm {...{
                course,
                formRef: courseFormRef,
                setFile: setImageFile,
                ...(isEdit ? {
                  setCourseChanges(func) {
                    setCourseChanges(func(courseChanges));
                  }
                }: {})
              }}/>
          )}
          {activeTab === 1 && (
            <CourseContentTab {...{
              modules: modules as IModule[], 
              setModules,
              formRef: contentFormRef,
              ...(isEdit? {
                async onDeleteModule(moduleId) {
                  if(isEdit){
                    setDeletedModuleIds(prev => [...prev, moduleId]); 
                  }
                  return true;
                },
                setLessonChanges(func) {
                  setLessonChanges(func(lessonChanges));
                },
                setModuleChanges(func) {
                  setModuleChanges(func(moduleChanges));
                },
              }: {})
            }} />
          )}
          {activeTab === 2 && (
            <AdditionalInfo
              formRef={extraFormRef}
              course={course}
              {...{
                ...(isEdit ? {
                  setCourseChanges(func) {
                    setCourseChanges(func(courseChanges));
                  }
                }: {})
              }}
            />
          )}
          <ButtonContainer>
            {activeTab === 0 ? <AppButton variant="secondary" onClick={onBack}>
              Cancel
            </AppButton> :
            <AppButton loading={processing} variant="secondary" onClick={() => onStepBack(true)}>
              Back
            </AppButton>}
            <AppButton loading={processing} variant="primary" onClick={isEdit ? handleUpdate: () => handleSubmit(true)}>
              {activeTab === 2 ? isEdit? "Update": "Submit" : "Continue"}
            </AppButton>
          </ButtonContainer>
        </div>
      </div>
    </Container>
  );
};
